import type { ApolloCache } from '@apollo/client';
import type { ApolloLink } from '@apollo/client/link';
import { useMutation, useQuery } from '@apollo/client/react';
import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { Navigate } from 'react-router';

import { Card } from '../../components/Card';
import { ContentCard } from '../../components/ContentCard';
import { ContentWrapper } from '../../components/ContentWrapper';
import { Loader } from '../../components/Loader';
import { apolloClient } from '../../config/apolloClients';
import { GenerateApiKeyDocument } from '../../graphql/mutations/__generated__/GenerateApiKey';
import type { GenerateApiKey } from '../../graphql/mutations/__generated__/GenerateApiKey';
import { RevokeApiKeyDocument } from '../../graphql/mutations/__generated__/RevokeApiKey';
import { GetApiKeysDocument } from '../../graphql/queries/__generated__/GetApiKeys';
import { getErrorMessage } from '../../helpers/formatUtils';
import { RAILS_ENV } from '../../helpers/globals';
import { ERROR_PAGE_ROUTE } from '../routes';

const MAX_KEYS = 2;

interface IKeyState {
  token: string;
  createdAt: string;
}

const handleRevokeUpdate = (key: string) => (cache: ApolloCache) => {
  const keys: any = cache.readQuery({ query: GetApiKeysDocument });

  if (keys) {
    cache.writeQuery({
      query: GetApiKeysDocument,
      data: {
        getPartnerAuthTokens: keys.getPartnerAuthTokens.filter(
          (keyObj: IKeyState) => keyObj.token !== key,
        ),
      },
    });
  }
};

const handleGenerateUpdate = (cache: ApolloCache, { data }: ApolloLink.Result<GenerateApiKey>) => {
  const keys: any = cache.readQuery({ query: GetApiKeysDocument });

  if (keys && data?.createPartnerAuthToken) {
    cache.writeQuery({
      query: GetApiKeysDocument,
      data: {
        getPartnerAuthTokens: keys.getPartnerAuthTokens.concat([
          { token: data.createPartnerAuthToken, createdAt: new Date(), __typename: '' },
        ]),
      },
    });
  }
};

const KeyModule = ({ token, createdAt }: { token: string; createdAt: Date }) => {
  const [revokeKey, { error }] = useMutation(RevokeApiKeyDocument, {
    update: handleRevokeUpdate(token),
    client: apolloClient,
  });

  const handleRevoke = () => {
    if (
      window.confirm(
        'Revoking this key will cause any requests made with key to be denied. ' +
          'Make sure this key is no longer in use before revoking it.',
      )
    ) {
      revokeKey({ variables: { token } });
    }
  };

  return (
    <Col key={token} className="pb-4">
      {error ? (
        <Text as="p" className="text-body-md text-od-content-secondary">
          Failed to revoke API key.&nbsp;{getErrorMessage(error)}
        </Text>
      ) : null}
      <Row className="max-w-full gap-2">
        <Card className="overflow-x-auto border-2 border-od-border-primary px-1.5 py-1">
          <Text as="span" className="text-label-sm">
            {token}
          </Text>
        </Card>
        <Button
          onClick={handleRevoke}
          variant="secondary"
          analyticsName="partnership-dashboard-fe-revoke-api-key"
          aria-label="Revoke API key"
        >
          Revoke
        </Button>
      </Row>
      <Text as="time" className="text-body-sm pt-1 text-od-content-tertiary">
        Created {createdAt.toLocaleDateString()} at {createdAt.toLocaleTimeString()}
      </Text>
    </Col>
  );
};

const GenerateButton = ({ maxedOut }: { maxedOut: boolean }) => {
  const [generateKey, { error }] = useMutation(GenerateApiKeyDocument, {
    update: handleGenerateUpdate,
    client: apolloClient,
  });

  return (
    <Col className="inline-flex pt-1">
      <Col>
        {error ? (
          <Text as="p" className="text-body-md text-od-content-secondary">
            Failed to generate API key.&nbsp;{getErrorMessage(error)}
          </Text>
        ) : null}
        <Button
          analyticsName="partnership-dashboard-fe-generate-api-key"
          variant="primary"
          className="w-full"
          onClick={() => {
            generateKey();
          }}
          disabled={maxedOut}
          aria-label="Generate new API key"
        >
          Generate new API key
        </Button>
      </Col>
    </Col>
  );
};

const KeySection = ({ keyStates }: { keyStates: Array<IKeyState> }) => {
  const maxedOut = keyStates.length >= MAX_KEYS;
  const heading = RAILS_ENV === 'production' ? 'Production' : 'Test';
  const name = RAILS_ENV === 'production' ? 'production' : 'test';
  return (
    <Col className="pt-6">
      <Text as="h4" className="text-subheader-xs pb-1">
        {heading}
      </Text>
      <Col className="pb-4">
        {keyStates.length > 0 ? (
          <Text as="p" className="text-body-md">
            Here are your active {name} keys
          </Text>
        ) : (
          <Text as="p" className="text-body-md">
            You don’t have a {name} key yet
          </Text>
        )}
      </Col>
      <Col className="pb-4">
        <Col>
          {keyStates.map(({ token, createdAt }) => (
            <KeyModule key={token} token={token} createdAt={new Date(createdAt)} />
          ))}
          <GenerateButton maxedOut={maxedOut} />
          {maxedOut && (
            <Text as="p" className="text-body-md pt-1">
              We allow a maximum of {MAX_KEYS} active keys. Please revoke a key if you need to
              generate a new one.
            </Text>
          )}
        </Col>
      </Col>
    </Col>
  );
};

const APIKeys = () => {
  const { loading, error, data } = useQuery(GetApiKeysDocument);

  return (
    <ContentWrapper>
      <ContentCard title="API Keys">
        <Col className="pt-2">
          <Text as="p" className="text-body-md">
            Your API keys are unique and sensitive. Keep them safe and never check them into your
            codebase. It is recommended to store and access these from your deployment’s environment
            variables.
          </Text>
        </Col>
        <Col className="pt-1">
          <a
            target="_blank"
            href="https://docs.opendoor.com/walkthroughs/make-your-first-request#adding-authorization-header"
            rel="noopener noreferrer"
          >
            Learn more →
          </a>
        </Col>
        {error ? (
          <Navigate to={ERROR_PAGE_ROUTE} replace />
        ) : loading ? (
          <Loader size="small" />
        ) : (
          <KeySection keyStates={data ? (data.getPartnerAuthTokens as IKeyState[]) : []} />
        )}
      </ContentCard>
    </ContentWrapper>
  );
};

export default APIKeys;
